import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Loader2, Check, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface NewsletterOptInProps {
  variant?: "footer" | "inline" | "modal" | "compact";
  source?: "footer" | "inline" | "modal" | "checkout" | "account" | "course-interest";
  className?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  tags?: string[];
  onSuccess?: (email: string) => void;
}

/**
 * Public newsletter opt-in widget. Premium sapphire styling, glass surface.
 * Calls newsletter-subscribe edge function. Records consent automatically.
 */
export function NewsletterOptIn({
  variant = "footer",
  source = "footer",
  className,
  title,
  description,
  ctaLabel,
  tags,
  onSuccess,
}: NewsletterOptInProps) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "success" | "error" | "rate_limited" | "suppressed">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim() || !/.+@.+\..+/.test(email)) {
      setState("error");
      setErrorMsg(t("newsletter.invalidEmail", { defaultValue: "Email inválido" }));
      return;
    }
    setState("loading");
    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: {
          email: email.trim().toLowerCase(),
          source,
          locale: i18n.language || "pt-BR",
          tags,
          consent_version: "v1",
        },
      });
      if (error) throw error;
      if (data?.error === "rate_limited") {
        setState("rate_limited");
        return;
      }
      if (data?.error === "suppressed") {
        setState("suppressed");
        return;
      }
      if (data?.ok) {
        setState("success");
        onSuccess?.(email);
        setEmail("");
        return;
      }
      throw new Error(data?.error || "unknown");
    } catch (err: any) {
      console.error("[NewsletterOptIn]", err);
      setState("error");
      setErrorMsg(err?.message || t("newsletter.genericError", { defaultValue: "Falha ao inscrever" }));
    }
  };

  const successMsg = t("newsletter.success", { defaultValue: "Inscrição confirmada! Obrigado." });
  const rateLimitedMsg = t("newsletter.rateLimited", { defaultValue: "Muitas tentativas. Tente novamente em alguns minutos." });
  const suppressedMsg = t("newsletter.suppressed", { defaultValue: "Este email não pode receber comunicações no momento." });

  const wrapperClass = cn(
    variant === "footer" && "w-full",
    variant === "inline" && "w-full max-w-xl mx-auto rounded-2xl glass-surface glass-border p-6 md:p-8",
    variant === "modal" && "w-full",
    variant === "compact" && "w-full",
    className,
  );

  const showHeader = variant !== "compact";

  return (
    <div className={wrapperClass}>
      {showHeader && (
        <div className="mb-4 space-y-1.5">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" aria-hidden />
            <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground">
              {title ?? t("newsletter.title", { defaultValue: "Newsletter Aicavite" })}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description ?? t("newsletter.description", { defaultValue: "Receba novidades, lançamentos de cursos e insights exclusivos." })}
          </p>
        </div>
      )}

      {state === "success" ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3.5">
          <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden />
          <p className="text-sm text-foreground">{successMsg}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-2.5" noValidate>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              required
              autoComplete="email"
              placeholder={t("newsletter.placeholder", { defaultValue: "seu@email.com" })}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state === "loading"}
              className="flex-1 h-10"
              aria-label={t("newsletter.emailLabel", { defaultValue: "Email" })}
            />
            <Button
              type="submit"
              disabled={state === "loading"}
              className="h-10 px-5 shrink-0"
            >
              {state === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                ctaLabel ?? t("newsletter.subscribe", { defaultValue: "Inscrever" })
              )}
            </Button>
          </div>

          {state === "error" && errorMsg && (
            <div className="flex items-start gap-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
              <span>{errorMsg}</span>
            </div>
          )}
          {state === "rate_limited" && (
            <p className="text-xs text-amber-500">{rateLimitedMsg}</p>
          )}
          {state === "suppressed" && (
            <p className="text-xs text-muted-foreground">{suppressedMsg}</p>
          )}

          <p className="text-[11px] text-muted-foreground/80 leading-relaxed pt-0.5">
            {t("newsletter.consent", { defaultValue: "Ao se inscrever, você concorda em receber emails da Aicavite. Você pode cancelar a qualquer momento." })}
          </p>
        </form>
      )}
    </div>
  );
}

export default NewsletterOptIn;
